import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Badge, Button } from 'reactstrap';
import SanitizeHTML from '@/shared/components/SanitizeHTML';
import { useDSCohortUIContext } from '../DSCohortUIProvider';
import DSCohortTableColumns, { DSCohortColumns } from './DSCohortTableColumns';
import { DSCohortTableSearchBar } from './DSCohortTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getDSCohortsList, setDSCohortFilters, toggleCheckedItemById } from '../../redux/actions';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import { FormGroup, Input } from 'reactstrap';

const DSCohortTable = () => {
  const dsCohortsUIContext = useDSCohortUIContext();
  const dispatch = useDispatch();
  const {
    data: dsCohorts,
    error,
    filters,
    loading,
    totalRecords,
    checkedDSCohortIds,
  } = useSelector((state) => state.cadsAIDSCohorts, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(DSCohortColumns.length);
  }, [DSCohortColumns.length]);

  React.useEffect(() => {
    dispatch(getDSCohortsList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setDSCohortFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (id) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => dsCohortsUIContext.openViewDSCohortDialog(id)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => dsCohortsUIContext.openEditDSCohortDialog(id)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setDSCohortFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setDSCohortFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - dsCohorts.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <DSCohortTableSearchBar />
        <Table className="material-table">
          <DSCohortTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading DS Cohorts...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!dsCohorts?.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No DS Cohorts'
                    : 'No DS Cohorts with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {dsCohorts?.map((dsCohort) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={dsCohort?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={dsCohort?.id}
                        defaultChecked={checkedDSCohortIds.includes(dsCohort?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    {dsCohort?.title}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <SanitizeHTML html={dsCohort?.description} />
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    {dsCohort?.program?.title}
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    {dsCohort?.noOfApplicants}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    <Badge
                      color={`${dsCohort?.status ? 'success' : 'danger'}`}
                      style={{ marginRight: 24 }}
                    >
                      {dsCohort?.status ? 'Active' : 'Deactivated'}
                    </Badge>
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(dsCohort?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default DSCohortTable;
